import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, ViewChild, OnInit, OnChanges, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'ng-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="searchable-select-container" [class.disabled]="disabled">
      <div class="search-input-container">
        <input 
          #searchInput
          type="text" 
          class="form-control search-input"
          [class.default-option-text]="showDefaultOption && showingDefaultOption && !hasValidSelection()"
          [class.selected-text]="!isFocused && hasValidSelection()"
          [value]="getInputDisplayValue()" 
          [placeholder]="isFocused ? '' : (placeholder || 'Select an option')"
          [style.display]="!isFocused && hasValidSelection() && hasHtmlContent() && !multiple ? 'none' : 'block'"
          [style.max-width]="maxWidth"
          [disabled]="disabled"
          (input)="onSearchChange($event)"
          (focus)="onFocus()"
          (blur)="onBlur()"
          (click)="onInputClick()"
          (keydown)="onKeyDown($event)"
          autocomplete="off"
        />
        <div 
          *ngIf="!isFocused && hasValidSelection() && hasHtmlContent() && !multiple"
          class="form-control search-input selected-html-content"
          (click)="onInputClick()"
          [innerHTML]="getSafeHtml(getSelectedItemHtml())">
        </div>
        <button 
          *ngIf="allowClear && (hasValidSelection() || (showDefaultOption && showingDefaultOption)) && !disabled" 
          type="button" 
          class="clear-button"
          (mousedown)="clearSelection($event)">
          <i class="fa fa-times"></i>
        </button>
        <i class="fa fa-chevron-down dropdown-arrow" 
           [class.rotated]="isDropdownOpen"
           (mousedown)="toggleDropdown($event)"></i>
      </div>
      
      <div class="dropdown-options" *ngIf="isDropdownOpen && filteredItems.length > 0">
        <div class="options-list">
          <div 
            *ngFor="let item of filteredItems; let i = index" 
            class="option-item"
            [class.selected]="isSelected(item)"
            [class.active]="(item.value || item.id).toString() === activeValue"
            (mousedown)="selectItem(item)">
            <div [innerHTML]="getSafeHtml(item.text || item.name)"></div>
          </div>
        </div>
      </div>
      
      <div class="dropdown-options" *ngIf="isDropdownOpen && filteredItems.length === 0 && searchTerm">
        <div class="no-options">
          No options found
        </div>
      </div>
    </div>
  `,
  styles: [`
    .searchable-select-container {
      position: relative;
    }
    
    .search-input-container {
      position: relative;
    }
    
    .search-input {
      padding-right: 100px;
      background-color: white;
      height: 48px;
      font-size: 1.1rem;
      padding: 12px 16px;
      border-radius: 4px;
      border: 1px solid #ced4da;
    }
    
    .search-input:focus {
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
      cursor: text;
    }
    
    .search-input.selected-text {
      font-weight: 600 !important;
      color: #000000 !important;
      font-size: calc(1.1rem - 2pt) !important;
    }
    
    .search-input::placeholder {
      font-size: calc(1.1rem - 2pt) !important;
    }
    
    .search-input::-webkit-input-placeholder {
      font-size: calc(1.1rem - 2pt) !important;
    }
    
    .search-input::-moz-placeholder {
      font-size: calc(1.1rem - 2pt) !important;
    }
    
    .search-input:-ms-input-placeholder {
      font-size: calc(1.1rem - 2pt) !important;
    }
    
    .clear-button {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #6c757d;
      padding: 4px;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      transition: all 0.2s;
      z-index: 10;
      font-size: 14px;
      pointer-events: auto;
    }
    
    .clear-button:hover {
      color: #dc3545;
      background-color: #f8f9fa;
    }
    
    .search-input-container:hover .clear-button {
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    
    .searchable-select-container:hover .clear-button {
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    
    .dropdown-arrow {
      position: absolute;
      right: 0px;
      top: 50%;
      transform: translateY(-50%);
      transition: transform 0.2s;
      color: #6c757d;
      cursor: pointer;
      padding: 8px;
      z-index: 3;
      font-size: 16px;
      display: inline-block;
      width: auto;
      height: auto;
      text-align: center;
      line-height: 1;
    }
    
    .dropdown-arrow.rotated {
      transform: translateY(-50%) rotate(180deg);
    }
    
    .dropdown-options {
      position: absolute;
      top: 100%;
      left: 0;
      background: white;
      border: 1px solid #ced4da;
      border-top: none;
      border-radius: 0 0 4px 4px;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
      z-index: 1000;
      min-height: 40px;
      width: auto;
      min-width: 100%;
      max-width: 800px;
      white-space: nowrap;
    }
    
    .options-list {
      max-height: 300px;
      overflow-y: auto;
      min-height: 40px;
      width: auto;
    }
    
    .option-item {
      padding: 6px 16px;
      cursor: pointer;
      border-bottom: none;
      transition: background-color;
      font-size: 1rem;
      user-select: none;
      min-height: 32px;
      display: flex;
      align-items: center;
      white-space: nowrap;
      width: auto;
    }
    
    .option-item:last-child {
      border-bottom: none;
    }
    
    .option-item:hover {
      background-color: #428bca !important;
      color: white !important;
    }
    
    .dropdown-options .option-item:hover {
      background-color: #428bca !important;
      color: white !important;
    }
    
    .searchable-select-container .dropdown-options .option-item:hover {
      background-color: #428bca !important;
      color: white !important;
    }
    
    .option-item.selected {
      background-color: #428bca !important;
      color: white !important;
      font-weight: 500 !important;
    }
    
    .option-item.selected:hover {
      background-color: #428bca !important;
      color: white !important;
    }
    
    .option-item.active {
      background-color: #428bca !important;
      color: white !important;
    }
    
    .no-options {
      padding: 10px;
      color: #6c757d;
      font-style: italic;
      text-align: center;
      font-size: 1rem;
      min-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      width: auto;
    }

    .default-option-text {
      font-weight: 600 !important;
      color: #000000 !important;
    }

    /* Bootstrap integration with square design */
    .searchable-select-container .form-control {
      display: block;
      width: 100%;
      height: 48px;
      padding: 12px 16px;
      font-size: 1.1rem;
      font-weight: 400;
      line-height: 1.4;
      color: #212529;
      background-color: #fff;
      background-image: none;
      border: 1px solid #ced4da;
      border-radius: 4px;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    }
    
    .searchable-select-container .form-control:focus {
      border-color: #007bff;
      border-width: 1px;
    }
    
    /* Square design adjustments */
    .searchable-select-container {
      min-height: 48px;
    }
    
    /* Make color squares smaller with better alignment */
    .searchable-select-container .option-item div[style*="background-color"] {
      display: block !important;
      height: 8px !important;
      width: 8px !important;
      border: 1px solid #ccc !important;
      box-sizing: border-box !important;
      flex-shrink: 0 !important;
      margin: 0 !important;
    }
    
    /* Ensure option items display properly with aligned content */
    .option-item > div {
      display: flex !important;
      align-items: center !important;
      line-height: 1.2 !important;
      gap: 4px !important;
    }
    
    /* Remove debug borders */
    .option-item div {
      border: none !important;
    }
    
    /* Selected HTML content styling with better alignment */
    .selected-html-content {
      cursor: pointer;
      display: flex !important;
      align-items: center !important;
      padding: 12px 40px 12px 16px;
      line-height: 1.2 !important;
      gap: 4px !important;
    }
    
    .searchable-select-container .selected-html-content div[style*="background-color"] {
      display: block !important;
      height: 8px !important;
      width: 8px !important;
      border: 1px solid #ccc !important;
      box-sizing: border-box !important;
      flex-shrink: 0 !important;
      margin: 0 !important;
    }

    /* Disabled state styling with ban cursor */
    .searchable-select-container.disabled {
      opacity: 0.6;
      cursor: not-allowed !important;
      position: relative;
    }

    .searchable-select-container.disabled * {
      cursor: not-allowed !important;
      pointer-events: none;
    }

    .searchable-select-container.disabled .search-input,
    .searchable-select-container.disabled .selected-html-content {
      background-color: #e9ecef !important;
      color: #6c757d !important;
      cursor: not-allowed !important;
    }

    .searchable-select-container.disabled .dropdown-arrow {
      color: #6c757d !important;
      cursor: not-allowed !important;
    }

    .searchable-select-container.disabled:hover {
      cursor: not-allowed !important;
    }

    /* Ensure the container itself can receive hover events */
    .searchable-select-container.disabled {
      pointer-events: auto !important;
    }

    .searchable-select-container.disabled *:not(.searchable-select-container) {
      pointer-events: none !important;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NgSelectComponent),
      multi: true
    }
  ]
})
export class NgSelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() items: any[] = [];
  @Input() active: any[] = [];
  @Input() placeholder: string = '';
  @Input() allowClear: boolean = false;
  @Input() searchable: boolean = true;
  @Input() showDefaultOption: boolean = false;
  @Input() maxWidth: string = '';
  @Input() multiple: boolean = false;
  @Output() selected = new EventEmitter<any>();
  @Output() removed = new EventEmitter<any>();

  @ViewChild('searchInput') searchInput!: ElementRef;

  activeValue: string = '';
  displayValue: string = '';
  searchTerm: string = '';
  isDropdownOpen: boolean = false;
  isFocused: boolean = false;
  previousValue: string = '';
  filteredItems: any[] = [];
  showingDefaultOption: boolean = true;
  disabled: boolean = false;
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }

  // Add this method for input keydown
  onKeyDown(event: KeyboardEvent): void {
    if (!this.isDropdownOpen || this.filteredItems.length === 0) return;
    const currentIndex = this.filteredItems.findIndex(item => (item.value || item.id).toString() === this.activeValue);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = currentIndex < this.filteredItems.length - 1 ? currentIndex + 1 : 0;
      const nextItem = this.filteredItems[nextIndex];
      if (nextItem) {
        this.activeValue = (nextItem.value || nextItem.id).toString();
        this.updateDisplayValue();
        this.cdr.detectChanges();
        setTimeout(() => this.scrollActiveItemIntoView(), 0);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : this.filteredItems.length - 1;
      const prevItem = this.filteredItems[prevIndex];
      if (prevItem) {
        this.activeValue = (prevItem.value || prevItem.id).toString();
        this.updateDisplayValue();
        this.cdr.detectChanges();
        setTimeout(() => this.scrollActiveItemIntoView(), 0);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selectedItem = this.filteredItems[currentIndex >= 0 ? currentIndex : 0];
      if (selectedItem) {
        this.selectItem(selectedItem);
      }
    }
  }

  scrollActiveItemIntoView(): void {
    const activeElement = this.elementRef.nativeElement.querySelector('.option-item.active');
    if (activeElement) {
      activeElement.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }

  private onChange = (value: any) => {};
  private onTouched = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  getSafeHtml(content: string): SafeHtml {
    // Add space between color square and text
    let modifiedContent = content || '';
    
    // Replace pattern: </div>Text with </div>&nbsp;Text (reduced spacing)
    modifiedContent = modifiedContent.replace(/<\/div>\s*([^<])/g, '</div>&nbsp;$1');
    
    return this.sanitizer.bypassSecurityTrustHtml(modifiedContent);
  }

  ngOnInit(): void {
    this.updateFilteredItems();
    
    // Initialize default option state
    if (this.showDefaultOption && !this.hasValidSelection()) {
      this.showingDefaultOption = true;
    }
  }

  ngOnChanges(): void {
    // Handle active input to set initial selection
    if (this.active && this.active.length > 0) {
      const activeItem = this.active[0];
      if (activeItem && activeItem.id) {
        this.activeValue = activeItem.id.toString();
        this.previousValue = this.activeValue;
        this.showingDefaultOption = false;
      }
    }
    
    this.updateFilteredItems();
    this.updateDisplayValue();
    
    // Reset default option state when active value changes
    if (this.showDefaultOption) {
      this.showingDefaultOption = !this.hasValidSelection();
    }
    
    // Force change detection for multiple mode to update display
    if (this.multiple) {
      this.cdr.detectChanges();
    }
  }

  toggleDropdown(event?: Event): void {
    if (this.disabled) return;
    
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    this.isDropdownOpen = !this.isDropdownOpen;
    
    if (this.isDropdownOpen) {
      // Focus the search input after the dropdown opens
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus();
        }
      }, 0);
    } else {
      this.closeDropdown();
    }
  }

  onInputClick(): void {
    if (this.disabled) return;
    
    if (!this.isDropdownOpen) {
      this.isDropdownOpen = true;
    }
    
    // Hide default option when dropdown opens
    if (this.showDefaultOption && this.showingDefaultOption) {
      this.showingDefaultOption = false;
      this.searchTerm = '';
      this.cdr.detectChanges(); // Force update immediately
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.searchTerm = '';
    this.updateFilteredItems();
    this.updateDisplayValue();
  }

  onFocus(): void {
    if (this.disabled) return;
    
    this.isFocused = true;
    // Store the current value before focusing
    this.previousValue = this.activeValue;
    
    if (!this.isDropdownOpen) {
      this.isDropdownOpen = true;
    }
    
    // Hide default option when focused
    if (this.showDefaultOption && this.showingDefaultOption) {
      this.showingDefaultOption = false;
      this.searchTerm = '';
      this.cdr.detectChanges(); // Force update
    }
  }

  getDisplayValue(): string {
    const selectedItem = this.items.find(item => 
      (item.value || item.id).toString() === this.activeValue
    );
    
    if (selectedItem) {
      return selectedItem.text || selectedItem.name;
    }
    
    if (this.showDefaultOption && this.showingDefaultOption && this.isDropdownOpen === false) {
      return 'Please select a dropdown item';
    }
    
    // Return current search term or empty string when dropdown is open or cleared
    return this.searchTerm || '';
  }

  getInputDisplayValue(): string {
    // When focused and dropdown is open, show search term or empty string for plain textbox
    if (this.isFocused && this.isDropdownOpen) {
      return this.searchTerm || '';
    }
    
    // Handle multiple selection mode
    if (this.multiple) {
      if (this.active && this.active.length > 0) {
        // Filter out any placeholder items (id -1)
        const validItems = this.active.filter(item => item.id !== -1);
        if (validItems.length > 0) {
          // Extract text from selected items
          const selectedTexts = validItems.map(item => {
            const content = item.text || item.name || '';
            // If it's HTML content with color squares, extract just the text part
            if (content.includes('<') && content.includes('>')) {
              const match = content.match(/<\/div>\s*(.+)$/);
              if (match) {
                return match[1].trim();
              }
              return content.replace(/<[^>]*>/g, '').trim();
            }
            return content;
          });
          return selectedTexts.join(', ');
        }
      }
      // No items selected in multiple mode
      return '';
    }
    
    // Single selection mode
    const selectedItem = this.items.find(item => 
      (item.value || item.id).toString() === this.activeValue
    );
    if (selectedItem) {
      const content = selectedItem.text || selectedItem.name || '';
      // If it's HTML content with color squares, extract just the text part
      if (content.includes('<') && content.includes('>')) {
        // Extract text after the closing div tag (for color items like "<div...></div> Yellow")
        const match = content.match(/<\/div>\s*(.+)$/);
        if (match) {
          return match[1].trim();
        }
        // Fallback: strip all HTML tags
        return content.replace(/<[^>]*>/g, '').trim();
      }
      return content;
    }
    if (this.showDefaultOption && this.showingDefaultOption && this.isDropdownOpen === false) {
      return 'Please select a dropdown item';
    }
    // Return current search term or empty string when dropdown is open or cleared
    return this.searchTerm || '';
  }

  hasValidSelection(): boolean {
    if (this.multiple) {
      // For multiple mode, check if there are any valid active items
      if (this.active && this.active.length > 0) {
        // Filter out placeholder items (id -1)
        const validItems = this.active.filter(item => item.id !== -1);
        return validItems.length > 0;
      }
      return false;
    }
    
    // For single mode, check if activeValue corresponds to a valid item
    const selectedItem = this.items.find(item => 
      (item.value || item.id).toString() === this.activeValue
    );
    return selectedItem !== undefined;
  }

  hasHtmlContent(): boolean {
    const selectedItem = this.items.find(item => 
      (item.value || item.id).toString() === this.activeValue
    );
    
    if (!selectedItem) return false;
    
    const content = selectedItem.text || selectedItem.name || '';
    return content.includes('<') && content.includes('>');
  }

  getSelectedItemHtml(): string {
    const selectedItem = this.items.find(item => 
      (item.value || item.id).toString() === this.activeValue
    );
    
    return selectedItem ? (selectedItem.text || selectedItem.name || '') : '';
  }

  onBlur(): void {
    this.isFocused = false;
    // If user didn't select anything and we have a previous value, restore it
    if (!this.searchTerm && this.previousValue) {
      this.activeValue = this.previousValue;
    }
    if (this.isDropdownOpen) {
      this.closeDropdown();
      this.onTouched();
    }
    // If no selection and default option enabled, show default option
    if (this.showDefaultOption && !this.hasValidSelection()) {
      this.showingDefaultOption = true;
      this.searchTerm = '';
      this.cdr.detectChanges(); // Force update
    }
  }

  onSearchChange(event: any): void {
    this.searchTerm = event.target.value.toLowerCase();
    this.displayValue = event.target.value;
    this.showingDefaultOption = false; // Hide default option when typing
    this.updateFilteredItems();
    
    if (!this.isDropdownOpen) {
      this.isDropdownOpen = true;
    }
  }

  updateFilteredItems(): void {
    if (!this.searchTerm) {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.items.filter(item => {
        const text = (item.text || item.name || '').toLowerCase();
        return text.includes(this.searchTerm);
      });
    }
  }

  selectItem(item: any): void {
    if (this.disabled) return;
    
    if (this.multiple) {
      // For multiple mode, emit selected and keep dropdown open
      this.selected.emit(item);
      this.searchTerm = '';
      this.updateFilteredItems();
      // Force change detection to update the display
      this.cdr.detectChanges();
      // Keep dropdown open for multiple selections
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    } else {
      // Single selection mode
      const value = item.value || item.id;
      this.activeValue = value ? value.toString() : '';
      this.previousValue = this.activeValue;
      this.searchTerm = '';
      this.showingDefaultOption = false;
      this.updateDisplayValue();
      this.updateFilteredItems();
      this.onChange(item);
      this.selected.emit(item);
      this.isDropdownOpen = false;
      if (this.searchInput) {
        this.searchInput.nativeElement.blur();
      }
    }
  }

  clearSelection(event: Event): void {
    if (this.disabled) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    // Clear all values to ensure placeholder shows
    this.activeValue = '';
    this.displayValue = '';
    this.searchTerm = '';
    this.showingDefaultOption = false;
    
    this.updateFilteredItems();
    this.onChange(null);
    this.removed.emit();
    this.isDropdownOpen = false;
    
    // Blur the input to trigger parent's change detection
    if (this.searchInput) {
      this.searchInput.nativeElement.blur();
    }
    
    // Force change detection
    this.cdr.detectChanges();
  }

  updateDisplayValue(): void {
    if (!this.activeValue) {
      this.displayValue = this.searchTerm;
      return;
    }

    const selectedItem = this.items.find(item => 
      (item.value || item.id).toString() === this.activeValue
    );

    this.displayValue = selectedItem ? (selectedItem.text || selectedItem.name) : this.searchTerm;
  }

  isSelected(item: any): boolean {
    if (!this.active || this.active.length === 0) {
      return false;
    }
    
    // Handle both single item and array formats
    const activeItems = Array.isArray(this.active) ? this.active : [this.active];
    return activeItems.some(activeItem => {
      if (typeof activeItem === 'object') {
        return (activeItem.value || activeItem.id) === (item.value || item.id);
      }
      return activeItem === (item.value || item.id);
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value) {
      this.activeValue = (value.value || value.id || value).toString();
    } else {
      this.activeValue = '';
    }
    this.updateDisplayValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.detectChanges();
  }
}
